<template>
  <ul v-if="fields.length > 0" class="choice-question__field-list">
    <li v-for="field in fields" :key="`field-${field.id}`" class="choice-question__field-container">
      <button
        v-if="field.type === 'choice'"
        :data-test-id="`ChoiceFields_Field-${field.id}`"
        :class="{
          'choice-question__field': true,
          'choice-question__field--selected': selectedFieldIds.has(field.id),
          'choice-question__field--right-align': isRtl,
        }"
        :name="`choice-question__field-${field.id}`"
        :disabled="
          maxAnswers > 1 && !selectedFieldIds.has(field.id) && selectedFieldIds.size >= maxAnswers
        "
        :dir="isRtl ? 'rtl' : 'ltr'"
        :value="field.id"
        :aria-selected="selectedFieldIds.has(field.id)"
        role="option"
        @click="$emit('fieldClicked', field.id)"
      >
        {{ field.title }}
      </button>
      <ChoiceTextField
        v-else-if="field.type === 'choiceText' && field.id === 'other'"
        ref="choiceTextField"
        :disabled="
          maxAnswers > 1 && !selectedFieldIds.has(field.id) && selectedFieldIds.size >= maxAnswers
        "
        :selected="selectedFieldIds.has('other')"
        @updated-text-choice="(textInput) => $emit('updatedTextChoice', textInput)"
      />
      <div
        v-if="maxAnswers > 1"
        :class="{
          'choice-question__tick-box': true,
          'choice-question__tick-box--selected': selectedFieldIds.has(field.id),
          'choice-question__tick-box--left': isRtl,
        }"
        @click="() => handleTickBoxClick(field)"
      />
    </li>
  </ul>
</template>
<script lang="ts">
import { type DeepReadonly, defineComponent, type PropType, ref } from 'vue'

import { isRtlLocale } from '../../../intl'
import { type Field } from '../../../question/models/Field'

import ChoiceTextField from './ChoiceTextField.vue'

export default defineComponent({
  components: {
    ChoiceTextField,
  },
  props: {
    fields: {
      required: true,
      type: Object as PropType<DeepReadonly<Field[]>>,
    },
    selectedFieldIds: {
      required: true,
      type: Set<string>,
    },
    maxAnswers: {
      required: true,
      type: Number,
    },
  },
  emits: {
    fieldClicked: (_fieldId: string) => true,
    updatedTextChoice: (_textInput: string) => true,
  },

  setup(_, ctx) {
    const choiceTextField = ref<InstanceType<typeof ChoiceTextField>[] | null>(null)
    const handleTickBoxClick = (field: DeepReadonly<Field>): void => {
      if (field.id === 'other') {
        choiceTextField.value?.[0]?.focusTextField()
        return
      }
      ctx.emit('fieldClicked', field.id)
    }
    return { isRtl: isRtlLocale(), handleTickBoxClick, choiceTextField }
  },
})
</script>
<style lang="postcss">
.choice-question {
  &__field-list {
    padding: 0;
    border: 1px solid var(--color-blue-grey);
    margin: 0;
    list-style: none;
  }

  &__field-container {
    position: relative;

    &:not(:last-child) {
      border-bottom: 1px solid var(--color-blue-grey);
    }
  }

  &__field {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem 3rem 1rem 0.85rem;
    border: 0;
    background-color: white;
    color: var(--color-text-button);
    cursor: pointer;
    font-size: var(--attest-font-size-s);
    text-align: left;
    transition:
      background-color 0.12s,
      color 0.12s;

    &--selected {
      background-color: var(--color-selected);
      color: white;
    }

    &--right-align {
      padding: 1rem 1rem 1rem 0.85rem;
      text-align: right;
    }

    &:disabled,
    &:disabled::placeholder {
      color: var(--color-disabled-text);
    }
  }

  &__tick-box {
    position: absolute;
    top: 0.65rem;
    right: 1rem;
    display: flex;
    width: 1.5rem;
    height: 1.5rem;
    justify-content: center;
    padding-top: 0.2rem;
    border: 1px solid var(--color-blue-grey);
    border-radius: 64px;
    cursor: pointer;

    &--selected {
      border-color: white;

      &::after {
        width: 0.3rem;
        height: 0.6rem;
        border-right: 2px white solid;
        border-bottom: 2px white solid;
        content: '';
        rotate: 45deg;
      }
    }

    &--left {
      right: unset;
      left: 1rem;
    }
  }
}
</style>
